/**
 * lead-attribution.js — A1 Compressor Warehouse
 * Captures UTM params, referrer, landing page, and session data
 * on every page load, then injects them as hidden fields into
 * any Shopify contact form before submission.
 *
 * Sources detected:
 *   paid_search    — utm_medium = cpc / ppc / paidsearch
 *   paid_social    — utm_medium = paid_social / paid-social
 *   organic_search — no UTM + referrer from known search engines
 *   organic_social — utm_medium = social / social-network
 *   email          — utm_medium = email
 *   agentic        — referrer from AI tools (ChatGPT, Perplexity, Claude, Gemini, Copilot, etc.)
 *   direct         — no UTM, no referrer
 *   referral       — everything else with a referrer
 */

(function () {
  'use strict';

  /* ── Constants ──────────────────────────────────────────── */
  const STORAGE_KEY = 'a1_attribution';
  const SESSION_KEY = 'a1_attribution_session';

  const SEARCH_ENGINES = [
    'google', 'bing', 'yahoo', 'duckduckgo', 'baidu',
    'yandex', 'ecosia', 'ask', 'aol', 'startpage',
  ];

  const AGENTIC_DOMAINS = [
    'chatgpt.com', 'chat.openai.com',
    'perplexity.ai',
    'claude.ai',
    'gemini.google.com', 'bard.google.com',
    'copilot.microsoft.com', 'bing.com/chat',
    'you.com',
    'phind.com',
    'poe.com',
    'character.ai',
    'grok.x.ai',
  ];

  const SOCIAL_DOMAINS = [
    'facebook.com', 'fb.com', 'instagram.com',
    'linkedin.com', 'twitter.com', 'x.com',
    'youtube.com', 'tiktok.com', 'pinterest.com',
    'reddit.com',
  ];

  /* ── Helpers ─────────────────────────────────────────────── */
  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name) || '';
  }

  function getReferrerHostname() {
    try {
      return new URL(document.referrer).hostname.replace(/^www\./, '');
    } catch (_) {
      return '';
    }
  }

  function matchesDomain(hostname, list) {
    return list.some(d => hostname === d || hostname.endsWith('.' + d));
  }

  function detectSource(utmSource, utmMedium, referrer) {
    const medium = utmMedium.toLowerCase();
    const source = utmSource.toLowerCase();

    if (medium) {
      if (['cpc', 'ppc', 'paidsearch', 'paid_search', 'paid-search'].includes(medium))
        return 'paid_search';
      if (['paid_social', 'paid-social', 'paid_social_ad'].includes(medium))
        return 'paid_social';
      if (['social', 'social-network', 'social_network', 'sm'].includes(medium))
        return 'organic_social';
      if (['email', 'e-mail', 'newsletter'].includes(medium))
        return 'email';
      if (['display', 'banner', 'cpm'].includes(medium))
        return 'display';
      return 'campaign'; // utm present but medium unrecognised
    }

    if (source) {
      if (SEARCH_ENGINES.some(s => source.includes(s))) return 'organic_search';
      if (SOCIAL_DOMAINS.some(s => source.includes(s.split('.')[0]))) return 'organic_social';
      if (AGENTIC_DOMAINS.some(s => source.includes(s.split('.')[0]))) return 'agentic';
      return 'referral';
    }

    // No UTMs — use referrer
    if (!referrer) return 'direct';
    if (matchesDomain(referrer, AGENTIC_DOMAINS)) return 'agentic';
    if (matchesDomain(referrer, SEARCH_ENGINES.map(s => s + '.com'))) return 'organic_search';
    if (matchesDomain(referrer, SOCIAL_DOMAINS)) return 'organic_social';
    if (referrer && !referrer.includes(window.location.hostname)) return 'referral';
    return 'direct';
  }

  /* ── Build attribution object ───────────────────────────── */
  function buildAttribution() {
    const utmSource   = getParam('utm_source');
    const utmMedium   = getParam('utm_medium');
    const utmCampaign = getParam('utm_campaign');
    const utmContent  = getParam('utm_content');
    const utmTerm     = getParam('utm_term');
    const gclid       = getParam('gclid');  // Google Ads auto-tag
    const msclkid     = getParam('msclkid'); // Microsoft Ads
    const fbclid      = getParam('fbclid');  // Meta Ads
    const referrer    = getReferrerHostname();

    const channel = detectSource(utmSource, utmMedium, referrer);

    return {
      channel,
      utm_source:   utmSource   || (gclid ? 'google' : msclkid ? 'bing' : fbclid ? 'facebook' : ''),
      utm_medium:   utmMedium   || (gclid ? 'cpc'    : msclkid ? 'cpc'  : fbclid ? 'paid_social' : ''),
      utm_campaign: utmCampaign,
      utm_content:  utmContent,
      utm_term:     utmTerm,
      referrer:     referrer,
      landing_page: window.location.pathname + window.location.search,
      gclid:        gclid,
      msclkid:      msclkid,
      fbclid:       fbclid,
      timestamp:    new Date().toISOString(),
    };
  }

  /* ── Persist (first-touch wins across pages) ────────────── */
  function loadStored() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch (_) { return null; }
  }

  function saveAttribution(attr) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attr)); } catch (_) {}
  }

  function getAttribution() {
    // Refresh only if this page has UTM params (new paid click mid-session)
    const hasUtm = ['utm_source','utm_medium','utm_campaign','gclid','msclkid','fbclid']
      .some(p => getParam(p));

    let stored = loadStored();
    if (!stored || hasUtm) {
      stored = buildAttribution();
      saveAttribution(stored);
    }
    return stored;
  }

  /* ── Inject hidden fields into a form ───────────────────── */
  function injectHiddenField(form, name, value) {
    // Re-use existing field if already injected
    let input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function stampForm(form, attr) {
    injectHiddenField(form, 'contact[attribution_channel]',  attr.channel);
    injectHiddenField(form, 'contact[attribution_source]',   attr.utm_source);
    injectHiddenField(form, 'contact[attribution_medium]',   attr.utm_medium);
    injectHiddenField(form, 'contact[attribution_campaign]', attr.utm_campaign);
    injectHiddenField(form, 'contact[attribution_content]',  attr.utm_content);
    injectHiddenField(form, 'contact[attribution_term]',     attr.utm_term);
    injectHiddenField(form, 'contact[attribution_referrer]', attr.referrer);
    injectHiddenField(form, 'contact[attribution_landing]',  attr.landing_page);
    injectHiddenField(form, 'contact[attribution_gclid]',    attr.gclid);
    injectHiddenField(form, 'contact[attribution_time]',     attr.timestamp);
  }

  /* ── Wire up all contact forms on the page ──────────────── */
  function wireForm(form) {
    if (form.dataset.a1AttributionWired) return;
    form.dataset.a1AttributionWired = '1';
    form.addEventListener('submit', function () {
      stampForm(form, getAttribution());
    }, { capture: true });
  }

  function wireForms() {
    // Shopify native contact forms
    document.querySelectorAll('form[action="/contact"]').forEach(wireForm);
    // Also catch any form with contact[] fields (quote modal uses method=post via Liquid)
    document.querySelectorAll('form').forEach(function (f) {
      if (f.querySelector('[name^="contact["]')) wireForm(f);
    });
  }

  /* ── Init ───────────────────────────────────────────────── */
  // Run attribution capture immediately (even before DOM ready)
  getAttribution();

  // Wire forms after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireForms);
  } else {
    wireForms();
  }

  // Also re-wire after Shopify/theme renders dynamic content (e.g. the quote modal)
  document.addEventListener('DOMContentLoaded', function () {
    // MutationObserver to catch dynamically added forms (modals, etc.)
    var observer = new MutationObserver(function () { wireForms(); });
    observer.observe(document.body, { childList: true, subtree: true });
  });

})();
